/**
 * Input Component
 *
 * Purpose: A styled input field component with consistent design system integration.
 *
 * Features:
 * - Consistent styling across the application
 * - Focus states with ring and border effects
 * - Hover effects for better user interaction
 * - Disabled state styling
 * - File input support
 * - Placeholder text styling
 * - Responsive typography (base on mobile, sm on desktop)
 *
 * Parameters:
 * @param type (optional) - HTML input type (text, email, password, etc.)
 * @param className (optional) - Additional CSS classes
 * @param ...props (optional) - All standard HTML input attributes
 *
 * Accessibility:
 * - Proper focus management with visible focus ring
 * - Disabled state support
 * - Compatible with form libraries and validation
 *
 * Design Integration:
 * - Uses design system colors (border, background, text)
 * - Consistent with other form components
 * - Smooth transitions for state changes
 */

import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

type InputProps = React.ComponentProps<'input'> & {
  showClear?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, showClear = false, onChange, ...props }, ref) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const [uncontrolledValue, setUncontrolledValue] = React.useState<string>(
      () =>
        typeof props.defaultValue === 'string' ||
        typeof props.defaultValue === 'number'
          ? String(props.defaultValue)
          : '',
    );

    const setRefs = (element: HTMLInputElement | null) => {
      internalRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    const isControlled = props.value !== undefined;
    const currentValue = isControlled
      ? typeof props.value === 'string' || typeof props.value === 'number'
        ? String(props.value)
        : ''
      : uncontrolledValue;
    const hasValue = currentValue.length > 0;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setUncontrolledValue(event.target.value);
      }
      onChange?.(event);
    };

    const clearValue = () => {
      const element = internalRef.current;
      if (!element || props.disabled || props.readOnly) {
        return;
      }

      const descriptor = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      );
      descriptor?.set?.call(element, '');

      const inputEvent = new Event('input', { bubbles: true });
      element.dispatchEvent(inputEvent);
      element.focus();

      if (!isControlled) {
        setUncontrolledValue('');
      }
    };

    return (
      <div className='relative w-full'>
        <input
          type={type}
          className={cn(
            'flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-all duration-300 focus:border-primary/50 focus:shadow-soft hover:border-primary/30',
            showClear ? 'pr-8' : '',
            className,
          )}
          ref={setRefs}
          onChange={handleChange}
          {...props}
        />

        {showClear && hasValue && !props.disabled && !props.readOnly ? (
          <button
            type='button'
            aria-label='Clear input'
            onClick={clearValue}
            className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none'
            tabIndex={-1}
          >
            <X className='h-3.5 w-3.5 mr-3' />
          </button>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
